<template>
    <div
        class="form-control form-imagehandler border"
        :class="[$attrs.class, { 'has-changes': hasChanges, 'has-data': currentfileBase64 != null }]"
    >
        <Dropzone
            v-if="value.Filename === null || value.Filename === undefined || value.Filename === ''"
            class="d-flex justify-content-center align-items-center px-3 py-2"
            accept="image/png, image/jpeg"
            @change="onFileListChanged"
            :multiple="false"
            ><label class="text-center"
                ><div class="flex-fill">
                    <div class="d-flex justify-content-center align-items-center gap-5">
                        <span
                            v-if="errorMessage !== null"
                            class="text-danger flex-shrink-1 ms-5"
                            >{{ $ti(errorMessage, { maxsize: formatByteSize(GetAppsetting('max_file_size')) }) }}</span
                        >
                        <span v-else>
                            <IconComp icon="fa-cloud-arrow-up"></IconComp>
                            <span class="ms-3">{{ $tm('Drop image here') }}</span>
                        </span>
                    </div>
                </div>
            </label>
        </Dropzone>
        <div v-else>
            <div class="d-flex gap-5 flex-fill position-relative">
                <div
                    class="flex-grow-1"
                    :class="{ 'px-3 py-2': !editeddata || !usethumbnail }"
                >
                    <span v-if="!editeddata || !usethumbnail">{{ currentfile?.name }}</span>
                    <div
                        class="cropper-bg"
                        v-else-if="editeddata && usethumbnail"
                    >
                        <img
                            :src="editeddata"
                            alt="image"
                            class="img-fluid"
                            :style="{ 'max-height': (imageheight ?? 99999) + 'px' }"
                        />
                    </div>
                </div>
                <div :class="{ 'position-absolute top-0 end-0': usethumbnail }">
                    <div class="d-flex">
                        <ButtonComp
                            v-if="currentfileBase64 && value.EntityState == 'Added'"
                            class="opacity-75"
                            title="Edit"
                            button-type="Secondary"
                            @click="editFile"
                            ><IconComp icon="fa-pencil-paintbrush"></IconComp>
                        </ButtonComp>
                        <ButtonComp
                            class="opacity-75"
                            title="Remove"
                            button-type="Secondary"
                            @click="removeFile"
                            ><IconComp icon="fa-xmark"></IconComp>
                        </ButtonComp>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <ModalComp
        ref="modal"
        title="imagehandler.editimage"
        size="lg"
    >
        <template #content>
            <ImageHandlerEditor
                v-if="currentfileBase64"
                :imagedata="currentfileBase64"
                v-model:editeddata="editeddata"
                v-model:aspectRatio="selectedAspectRatio"
                :aspectRatios="props.aspectRatios"
                :imageheight="props.imageheight"
            ></ImageHandlerEditor>
        </template>

        <template #footer>
            <ButtonComp
                button-type="Secondary"
                @click="modal?.hide()"
                >{{ $tm('button.close') }}</ButtonComp
            >
        </template>
    </ModalComp>
</template>

<script lang="ts" setup>
import { computed, ref, watch, type ComponentInstance } from 'vue';
import { ButtonComp, Dropzone, IconComp, ModalComp } from '.';
import ImageHandlerEditor from './views/ImageHandlerEditor.vue';
import { FileDetails } from '@/objects/generated';
import { $ti, $tm } from '@/locize';
import type { SelectLine } from '@/objects';
import { GetAppsetting } from '@/composeables';
import { formatByteSize } from '@/utilities/utils';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        modelValue: FileDetails;

        /**
         * the aspect ratio of the image
         * @default 1 i.e 1:1 (height:width)
         */
        aspectRatio?: number;

        /**
         * The aspect ratios we can select
         */
        aspectRatios?: SelectLine[] | null;

        /**
         * Whether to show the details of the image automatically when adding a new image
         * @default false
         */
        showdetails?: boolean;

        /**
         * The height of the image we will return
         * @default 100
         */
        imageheight?: number | null;

        /**
         * Whether to use the thumbnail or show the title.
         * @default true
         */
        usethumbnail?: boolean;
    }>(),
    {
        aspectRatio: 1,
        imageheight: 100,
        showdetails: false,
        usethumbnail: true,
        aspectRatios: null,
    },
);

const selectedAspectRatio = ref<number>(props.aspectRatio);

/**
 * Emit the update:modelValue event
 */
const emit = defineEmits<{
    (e: 'update:modelValue', val: FileDetails): void;
    (e: 'changes', value: boolean): void;
    (e: 'fileadded', value: FileDetails): void;
    (e: 'removefile', value: FileDetails): void;
}>();

/**
 * Define the internal model value
 */
const value = computed<FileDetails>({
    get: () => props.modelValue,
    set: (val: FileDetails) => {
        emit('update:modelValue', val);
    },
});

/**
 * The modal reference
 * We use it to show the modal
 */
const modal = ref<ComponentInstance<typeof ModalComp> | null>(null);

/**
 * Error message to show when the file uploaded is not supported or too large
 */
const errorMessage = ref<string | null>(null);

/**
 * Helper property to keep track of changes
 */
const hasChanges = ref(false);
const setChanges = (val: boolean) => {
    hasChanges.value = val;
    emit('changes', hasChanges.value);
};

/**
 * The file details of the current file
 */
const currentfile = ref<File | null>(null);

/**
 * Contains the edited data from the editor. This is the content of the preview
 */
const editeddata = ref<string | null>(null);

/**
 * Base64 representation of the current file
 */
const currentfileBase64 = ref<string | null>(null);

const removeFile = () => {
    value.value.Base64Data = null;
    value.value.Filename = null;
    value.value.MimeType = null;
    value.value.Extension = null;
    value.value.Delete();
    currentfile.value = null;
    currentfileBase64.value = null;
    setChanges(false);

    emit('removefile', value.value);
};

/**
 * Edit the file
 * Opens the modal to edit the file
 */
const editFile = () => {
    modal.value?.show();
};

/**
 * When the edited data changes
 * We will update the value
 */
watch(editeddata, () => {
    value.value.Base64Data = editeddata.value ?? '';
    setChanges(true);
});

watch(
    () => props.modelValue,
    (val) => {
        if (val.Base64Data) {
            currentfileBase64.value = val.Base64Data;
            editeddata.value = val.Base64Data;
        }
    },
    { immediate: true },
);

/**
 * When the file list changes
 * @param files
 */
const onFileListChanged = (files: File[]) => {
    if (files.length === 0) {
        currentfile.value = null;
        return;
    }

    // We only allow files less than 1MB
    if (files[0].size > GetAppsetting('max_file_size')) {
        errorMessage.value = 'imagehandler.maxfilesize';
        return;
    }

    // Check the filetype, should only accept jpg and png
    if (!['image/jpeg', 'image/png'].includes(files[0].type)) {
        errorMessage.value = 'imagehandler.invalidfiletype';
        return;
    }

    currentfile.value = files[0];
    value.value.Filename = files[0].name;
    value.value.MimeType = files[0].type;
    value.value.Extension = files[0].name.split('.').pop() ?? '';
    value.value.MaxHeight = props.imageheight ?? 0;

    let fr = new FileReader();
    fr.onload = (e) => {
        currentfileBase64.value = e.target?.result as string;
        value.value.Base64Data = currentfileBase64.value ?? '';

        emit('fileadded', value.value);
    };
    fr.readAsDataURL(currentfile.value);

    if (props.showdetails) {
        editFile();
    }
    setChanges(true);
};
</script>
